'use client';

import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';

import { MOTION_CONTROL_LABELS } from '@/content/a11y-ui';
import { cn } from '@/lib/utils';

import { ReelVideoDialog } from './ReelLightbox';

interface ReelTeaserProps {
  /** Full self-hosted reel, played (with sound) in the fullscreen dialog. */
  videoSrc: string;
  /** Local poster frame (fallback + pre-play placeholder). */
  posterSrc: string;
  /**
   * Self-hosted teaser loop (10–15s MP4). Plays muted; when the file has
   * an audio track (`teaserHasAudio`), an unmute control toggles its sound
   * in place. If the file is missing or fails to load, the poster renders.
   */
  teaserSrc: string;
  /** Accessible name for the trigger, e.g. "Watch the EVDEV showreel" */
  ariaLabel: string;
  /** Screen-reader label for the play affordance, e.g. "Watch reel" */
  hoverLabel: string;
  /** Lighter encode the dialog uses on phones. */
  mobileVideoSrc?: string;
  /** `data-analytics-location` for the trigger. */
  analyticsLocation?: string;
  /** Extra classes for the outer wrapper (width, placement). */
  className?: string;
  /**
   * Hold the first play until the page has finished loading and the main
   * thread is idle. For above-the-fold placements, so the teaser never
   * competes with the hero for bandwidth.
   */
  deferPlayback?: boolean;
  /**
   * Show the unmute toggle. Only for a teaser file that carries an audio
   * track — the v11 teaser is encoded silent, so the toggle would do nothing.
   */
  teaserHasAudio?: boolean;
}

/**
 * Silent looping video teaser (hero corner card, showreel band) that opens
 * the fullscreen reel dialog on click. The why-choose section uses
 * `ReelPlayer` instead; the 1.5x hover zoom that lived here for it covered
 * the content beside the card and was removed in the Oct 2026 redesign.
 *
 * Plays only while in the viewport (IntersectionObserver pauses it off-screen), renders a static poster under
 * prefers-reduced-motion or when the asset is unavailable, and exposes an
 * unmute toggle that fades in on hover — sound starts from the top of the
 * clip so the intro is coherent.
 */
export function ReelTeaser({
  videoSrc,
  posterSrc,
  teaserSrc,
  ariaLabel,
  hoverLabel,
  mobileVideoSrc,
  analyticsLocation = 'hero',
  className,
  deferPlayback = false,
  teaserHasAudio = false,
}: ReelTeaserProps) {
  const [open, setOpen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  /** Visitor pressed pause (WCAG 2.2.2): the loop stays stopped until play. */
  const [userPaused, setUserPaused] = useState(false);
  const userPausedRef = useRef(false);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const [pageReady, setPageReady] = useState(!deferPlayback);

  useEffect(() => {
    if (!deferPlayback) return;
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const arm = () => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(() => setPageReady(true), { timeout: 3000 });
      } else {
        timeoutId = setTimeout(() => setPageReady(true), 1500);
      }
    };
    if (document.readyState === 'complete') arm();
    else window.addEventListener('load', arm, { once: true });
    return () => {
      window.removeEventListener('load', arm);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [deferPlayback]);

  const showVideo = !videoFailed && !reducedMotion;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo || !pageReady) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        if (entry.isIntersecting) {
          if (userPausedRef.current) return;
          video.play().catch(() => setVideoFailed(true));
        } else {
          video.pause();
          // Off-screen: return to muted so sound never plays unseen.
          video.muted = true;
          setMuted(true);
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, [showVideo, pageReady]);

  const toggleMute = useCallback((e: React.MouseEvent) => {
    // The whole card opens the dialog — keep this click local.
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    const next = !video.muted;
    video.muted = next;
    if (!next) {
      // Unmuting mid-loop drops the viewer into mid-sentence; restart.
      video.currentTime = 0;
      video.play().catch(() => undefined);
    }
    setMuted(next);
  }, []);

  const togglePause = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    const next = !userPausedRef.current;
    userPausedRef.current = next;
    setUserPaused(next);
    if (!video) return;
    if (next) video.pause();
    else video.play().catch(() => undefined);
  }, []);

  return (
    <>
      {/* Wrapper owns the hover/scale/shadow so the card trigger and the
          unmute control can be SIBLINGS. They used to be nested — a
          `role="button"` span inside the card <button> — which axe flags as
          `nested-interactive` (serious, WCAG 4.1.2): a control inside a
          control has no reliable name/role/state for assistive tech, and
          `<button>` inside `<button>` is invalid HTML besides. Keeping the
          transform here means both still scale together on hover. */}
      <div
        className={cn(
          'group relative block w-full rounded-2xl shadow-[0_4px_30px_rgba(0,0,0,0.08)] transition-all duration-300 ease-out lg:hover:shadow-[0_24px_60px_rgba(0,0,0,0.45)]',
          className,
        )}
      >
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={ariaLabel}
        data-analytics-cta={`${analyticsLocation}_reel_teaser`}
        data-analytics-location={analyticsLocation}
        className="block w-full overflow-hidden rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
      >
        <span className="relative block aspect-video w-full">
          {showVideo ? (
            <video
              ref={videoRef}
              src={teaserSrc}
              poster={posterSrc}
              muted
              loop
              playsInline
              preload="none"
              onError={() => setVideoFailed(true)}
              className="size-full object-cover"
              aria-hidden="true"
              tabIndex={-1}
            />
          ) : (
            <Image
              src={posterSrc}
              alt=""
              fill
              sizes="(max-width: 1024px) 90vw, 420px"
              className="object-cover"
            />
          )}

          {/* Play affordance: glyph only, top-right — the reel has burned-in
              subtitles along the bottom edge that a labeled pill covered. */}
          <span className="absolute right-3 top-3 flex size-10 items-center justify-center rounded-full bg-accent text-[#121212] shadow-md transition-transform duration-300 group-hover:scale-110 lg:size-11">
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              className="ml-0.5 size-4"
              aria-hidden="true"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
            <span className="sr-only">{hoverLabel}</span>
          </span>

          {/* Unmute toggle: fades in on hover (always visible on touch,
              which has no hover). Rendered as a span with a click handler
              because the card itself is already a <button>. */}
        </span>
      </button>

      {showVideo && (
        <button
          type="button"
          onClick={togglePause}
          aria-pressed={userPaused}
          aria-label={userPaused ? MOTION_CONTROL_LABELS.playVideo : MOTION_CONTROL_LABELS.pauseVideo}
          className="absolute left-3 top-3 z-10 flex size-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <svg viewBox="0 0 24 24" fill="currentColor" className="size-3.5" aria-hidden="true">
            {userPaused ? <path d="M8 5v14l11-7z" /> : <path d="M7 5h4v14H7zM13 5h4v14h-4z" />}
          </svg>
        </button>
      )}

      {showVideo && teaserHasAudio && (
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? 'Unmute teaser' : 'Mute teaser'}
          className="absolute bottom-3 right-3 z-10 flex size-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-all duration-300 hover:bg-black/75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100"
        >
          {muted ? (
            <svg viewBox="0 0 24 24" fill="currentColor" className="size-4" aria-hidden="true">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.59 3 2.7-2.7-1.41-1.42-2.7 2.71-2.7-2.7-1.42 1.41 2.71 2.7-2.7 2.7 1.41 1.42 2.7-2.71 2.7 2.7 1.42-1.41-2.71-2.7z" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="currentColor" className="size-4" aria-hidden="true">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
            </svg>
          )}
        </button>
      )}
      </div>

      {open && (
        <ReelVideoDialog
          src={videoSrc}
          mobileSrc={mobileVideoSrc}
          poster={posterSrc}
          ariaLabel={ariaLabel}
          onClose={close}
        />
      )}
    </>
  );
}
